/**
 * Record Payment Modal
 * Record insurance payments with optional proof document
 */

import React, { useState } from 'react';
import { X, DollarSign, AlertCircle, Upload } from 'lucide-react';
import { MonthlyInvoiceDetail } from '../../services/monthlyInvoiceService';
import monthlyInvoiceService from '../../services/monthlyInvoiceService';

interface RecordPaymentModalProps {
  invoice: MonthlyInvoiceDetail;
  onClose: () => void;
  onPaymentRecorded: () => void;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  invoice,
  onClose,
  onPaymentRecorded,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Virement',
    reference: '',
    notes: '',
  });
  const [proofDocument, setProofDocument] = useState<File | null>(null);

  const remaining = parseFloat(invoice.net_to_pay) - parseFloat(invoice.amount_paid);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await monthlyInvoiceService.recordPayment(invoice.id, {
        ...formData,
        proof_document: proofDocument || undefined,
      });
      onPaymentRecorded();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement du paiement');
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProofDocument(e.target.files[0]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Enregistrer Paiement</h2>
            <p className="text-sm text-gray-600 mt-1">{invoice.numero}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary */}
        <div className="p-6 bg-blue-50 border-b">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Net à payer:</span>
              <div className="font-semibold text-blue-900 mt-1">
                {monthlyInvoiceService.formatCurrency(invoice.net_to_pay)}
              </div>
            </div>
            <div>
              <span className="text-blue-700">Déjà payé:</span>
              <div className="font-semibold text-blue-900 mt-1">
                {monthlyInvoiceService.formatCurrency(invoice.amount_paid)}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-blue-200">
            <span className="text-blue-700">Solde restant:</span>
            <div className="text-xl font-bold text-green-600 mt-1">
              {monthlyInvoiceService.formatCurrency(remaining)}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Montant (GNF) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={remaining}
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="0.00"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Maximum: {monthlyInvoiceService.formatCurrency(remaining)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de paiement *</label>
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Méthode de paiement *</label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
            >
              <option value="Virement">Virement bancaire</option>
              <option value="Chèque">Chèque</option>
              <option value="Espèces">Espèces</option>
              <option value="Mobile Money">Mobile Money</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Référence</label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="N° de virement, chèque, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Justificatif de paiement
            </label>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                id="proof-document"
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="proof-document"
                className="flex flex-col items-center cursor-pointer"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {proofDocument ? proofDocument.name : 'Cliquez pour uploader'}
                </span>
                <span className="text-xs text-gray-500 mt-1">PDF, JPG ou PNG</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Informations complémentaires..."
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              <DollarSign className="w-4 h-4" />
              {loading ? 'Enregistrement...' : 'Enregistrer Paiement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
